import React, { useRef, useState } from "react";
import { FileText, Image as ImageIcon, Upload, Trash2, FileEdit, Paperclip, Link2 } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import ImageUploadField from "modules/swiftboard/components/ui/ImageUploadField";
import { useAlert } from "context/AlertContext";
import { uploadSingleFile } from "services/upload-service/upload-service";

// Settings panels for the article builder's non-Content sidebar steps.

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text_muted mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-[11px] text-text_muted">{hint}</p>}
  </div>
);

const inputClass =
  "w-full px-3 py-2 text-sm rounded-lg outline-none border border-border bg-transparent text-text_primary focus:border-border_strong focus:ring-2 focus:ring-border";

const StepHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-primary_tint flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-primary" />
    </div>
    <div>
      <h3 className="text-[15px] font-semibold text-text_primary">{title}</h3>
      <p className="text-[12.5px] text-text_muted mt-0.5">{description}</p>
    </div>
  </div>
);

const SectionWrap = ({ icon, title, description, children }) => (
  <div className="max-w-2xl mx-auto space-y-5">
    <StepHeader icon={icon} title={title} description={description} />
    <div className="bg-surface border border-border rounded-2xl p-6 space-y-5 shadow-sm">{children}</div>
  </div>
);

const BANNER_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const BANNER_MAX_SIZE_MB = 10;

// Single-image upload for the article banner — delegates the compress + S3
// presigned-url upload flow to the shared ImageUploadField component.
const BannerImageField = ({ value, onChange }) => (
  <Field label="Banner Image" hint="Shown as the cover image wherever this article is listed.">
    <ImageUploadField
      value={value}
      onChange={onChange}
      acceptedTypes={BANNER_ACCEPTED_TYPES}
      maxSizeMB={BANNER_MAX_SIZE_MB}
      projectTitle="knowledge-article-banner"
      emptyLabel="Upload a banner image"
      allowUrl={false}
    />
  </Field>
);

export const InformationSection = ({
  meta,
  onChange,
  knowledgeBaseOptions,
  loadingKnowledgeBases,
  bannerImage,
  onBannerImageChange,
  slug,
}) => (
  <SectionWrap
    icon={FileEdit}
    title="Basic Information"
    description="Core details shown wherever this article is listed."
  >
    <Field label="Description">
      <textarea
        rows={3}
        className={`${inputClass} resize-none`}
        placeholder="Short summary of this article..."
        value={meta.description}
        onChange={(e) => onChange({ description: e.target.value })}
      />
    </Field>
    <Field label="Knowledge Base">
      <SearchableSelect
        options={knowledgeBaseOptions}
        value={meta.knowledgeBaseId}
        onChange={(val) => onChange({ knowledgeBaseId: val })}
        placeholder="Select a knowledge base"
        isClearable
        isLoading={loadingKnowledgeBases}
      />
    </Field>
    <Field
      label="Display Order"
      hint="Controls where this article falls among the others in its chapter. Leave blank to add it at the end."
    >
      <input
        type="number"
        min={0}
        className={inputClass}
        placeholder="Auto (end of chapter)"
        value={meta.order ?? ""}
        onChange={(e) => onChange({ order: e.target.value })}
      />
    </Field>
    <BannerImageField value={bannerImage} onChange={onBannerImageChange} />
    {slug && (
      <Field label="URL Slug" hint="Generated automatically from the title — updates when the title changes.">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-surface_alt/40">
          <Link2 size={13} className="text-text_muted flex-shrink-0" />
          <span className="text-[13px] text-text_secondary truncate">{slug}</span>
        </div>
      </Field>
    )}
  </SectionWrap>
);

const ATTACHMENT_ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const ATTACHMENT_MAX_SIZE_MB = 20;

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

const makeAttachmentId = () => `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Multi-file uploader for reference attachments — each file uploads straight
// to S3 via the shared presigned-url upload service (same mechanism the
// banner image field above uses). `attachments` is the parent's list of
// already-uploaded files ({file_name, file_url, file_size, file_type});
// `onChange` is called with that same shape whenever the completed set
// changes (add, remove, a pending upload finishing).
export const AttachmentsSection = ({ attachments, onChange }) => {
  const { showAlert } = useAlert();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState(() =>
    (attachments || []).map((a) => ({
      id: makeAttachmentId(),
      file: null,
      name: a.file_name,
      size: a.file_size,
      type: a.file_type,
      status: "completed",
      progress: 100,
      file_url: a.file_url,
      error: null,
    }))
  );

  const emitChange = (list) => {
    onChange(
      list
        .filter((f) => f.status === "completed" && f.file_url)
        .map((f) => ({ file_name: f.name, file_url: f.file_url, file_size: f.size, file_type: f.type }))
    );
  };

  const updateFiles = (updater) => {
    setFiles((prev) => {
      const next = updater(prev);
      emitChange(next);
      return next;
    });
  };

  const uploadFile = async (fileItem) => {
    setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: "uploading", progress: 0 } : f)));
    try {
      const result = await uploadSingleFile({
        file: fileItem.file,
        directory: "knowledge-article-attachments",
        projectTitle: "knowledge-article-attachment",
        onProgress: (pct) => setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, progress: pct } : f))),
      });

      if (!result.success) {
        updateFiles((prev) =>
          prev.map((f) => (f.id === fileItem.id ? { ...f, status: "failed", error: "Upload failed" } : f))
        );
        return;
      }
      updateFiles((prev) =>
        prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: "completed", progress: 100, file_url: result.file_url } : f
        )
      );
    } catch {
      updateFiles((prev) =>
        prev.map((f) => (f.id === fileItem.id ? { ...f, status: "failed", error: "Upload failed" } : f))
      );
    }
  };

  const addFiles = (rawFiles) => {
    const accepted = [];
    for (const file of rawFiles) {
      if (file.size > ATTACHMENT_MAX_SIZE_MB * 1024 * 1024) {
        showAlert({ type: "warning", message: `${file.name} exceeds ${ATTACHMENT_MAX_SIZE_MB} MB and was skipped.` });
        continue;
      }
      if (!ATTACHMENT_ACCEPTED_TYPES.includes(file.type)) {
        showAlert({ type: "warning", message: `${file.name} is not a supported file type.` });
        continue;
      }
      accepted.push({
        id: makeAttachmentId(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: "pending",
        progress: 0,
        file_url: null,
        error: null,
      });
    }
    if (!accepted.length) return;
    setFiles((prev) => [...prev, ...accepted]);
    accepted.forEach((item) => uploadFile(item));
  };

  const removeFile = (id) => updateFiles((prev) => prev.filter((f) => f.id !== id));
  const retryFile = (fileItem) => uploadFile(fileItem);

  return (
    <SectionWrap icon={Paperclip} title="Attachments" description="Reference files linked to this article.">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPTED_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          addFiles(Array.from(e.target.files));
          e.target.value = "";
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(Array.from(e.dataTransfer.files));
        }}
        className={`border-2 border-dashed rounded-xl p-8 text-center ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-surface_alt/30"
        }`}
      >
        <Upload size={22} className="mx-auto text-text_muted" />
        <p className="mt-2.5 text-[13px] font-medium text-text_secondary">Drag &amp; drop files here</p>
        <p className="text-[11px] text-text_muted mt-0.5">
          PDF, Word, Excel, ZIP, images · up to {ATTACHMENT_MAX_SIZE_MB} MB each
        </p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg shadow-sm"
        >
          <Upload size={13} />
          Choose Files
        </button>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className={`relative rounded-xl border overflow-hidden ${
                file.status === "completed"
                  ? "border-success_border bg-success_bg"
                  : file.status === "failed"
                  ? "border-danger_border bg-danger_bg"
                  : "border-border"
              }`}
            >
              {file.status === "uploading" && (
                <div className="absolute inset-0 bg-primary/5" style={{ width: `${file.progress}%` }} />
              )}
              <div className="relative flex items-center gap-3 px-3.5 py-2.5">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-surface_alt flex items-center justify-center">
                  {file.type?.startsWith("image/") ? (
                    <ImageIcon size={15} className="text-primary" />
                  ) : (
                    <FileText size={15} className="text-danger" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-text_primary truncate">{file.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-text_muted">{formatFileSize(file.size)}</span>
                    {file.status === "uploading" && (
                      <span className="text-[11px] text-primary font-medium">{file.progress}%</span>
                    )}
                    {file.status === "completed" && (
                      <span className="text-[11px] text-success font-medium">Ready</span>
                    )}
                    {file.status === "failed" && (
                      <button
                        type="button"
                        onClick={() => retryFile(file)}
                        className="text-[11px] text-danger font-semibold"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  className="flex-shrink-0 p-1 rounded-md text-text_muted"
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionWrap>
  );
};
